import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, List, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { useReferenceState } from "lib/@hooks/use-reference"
import { AuthoringCollapseAllToggle } from "authoring/components/authoring-collapse-all-toggle"
import { ScheduleCommentaryLegislationItem } from "authoring/routes/project/schedule-commentary/components/schedule-commentary-legislation-item"
import { ScheduleCommentaryHideUnchangedToggle } from "authoring/routes/project/schedule-commentary/components/schedule-commentary-hide-unchanged-toggle"
import { scheduleCommentaryLabel, scheduleCommentaryVersionLabel } from "../lib/schedule-commentary-label"
import { ScheduleCommentaryPreviewSection } from "./schedule-commentary-preview-section"

export function ScheduleCommentaryPreviewDialog({ open, onClose, item }) {
    // Keyed by item.id (the snapshot's own _id) rather than a draftId - this dialog has no
    // projectId to build one from, but the snapshot id is just as stable a persistence key.
    const [collapsedKeyArray, setCollapsedKeyArray] = useReferenceState(
        `scheduleCommentaryPreview-collapsedSections-${item.id}`,
        []
    )
    const [hideUnchanged] = useReferenceState(`scheduleCommentaryPreview-hideUnchanged-${item.id}`, false)
    const collapsedKeys = new Set(collapsedKeyArray)
    const visibleSections = hideUnchanged ? item.sections.filter((note) => note.hasAmends) : item.sections
    const sectionKeys = visibleSections.map((note) => note.amendsKey)
    const allCollapsed = sectionKeys.length > 0 && sectionKeys.every((key) => collapsedKeys.has(key))
    const description = scheduleCommentaryVersionLabel(item)

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" data-cy="schedule-commentary-preview-dialog">
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        {item.scheduleTitle ?? item.scheduleId}
                        <If value={Boolean(description)}>
                            <Typography variant="body2" color="text.secondary">
                                {description}
                            </Typography>
                        </If>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label={`Close preview of ${scheduleCommentaryLabel(item)}`}
                        data-cy="schedule-commentary-preview-close"
                    >
                        <Iconify icon="mdi:close" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <If value={sectionKeys.length > 0}>
                        <AuthoringCollapseAllToggle
                            expanded={!allCollapsed}
                            onToggle={toggleAllCollapsed}
                            label="sections"
                            dataCy="schedule-commentary-preview-collapse-toggle"
                        />
                    </If>
                    <ScheduleCommentaryHideUnchangedToggle
                        storageKey={`scheduleCommentaryPreview-hideUnchanged-${item.id}`}
                        dataCy="schedule-commentary-preview-hide-unchanged-toggle"
                    />
                </Stack>
                <Stack spacing={2}>
                    <Repeat
                        list={visibleSections}
                        item={
                            <ScheduleCommentaryPreviewSection
                                collapsedKeys={collapsedKeys}
                                onToggleSection={toggleSection}
                            />
                        }
                        keyFn={(note) => note.amendsKey}
                    />
                    <If value={item.legislation?.length > 0}>
                        <Box>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Hyperlinks
                            </Typography>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Legislation, Regulations and Guidance
                            </Typography>
                            <List sx={{ listStyleType: "disc", pl: 3 }}>
                                <Repeat list={item.legislation} item={<ScheduleCommentaryLegislationItem />} />
                            </List>
                        </Box>
                    </If>
                </Stack>
            </DialogContent>
        </Dialog>
    )

    function toggleAllCollapsed() {
        setCollapsedKeyArray(allCollapsed ? [] : sectionKeys)
    }

    function toggleSection(amendsKey) {
        setCollapsedKeyArray((current) => {
            const next = new Set(current)
            if (next.has(amendsKey)) next.delete(amendsKey)
            else next.add(amendsKey)
            return [...next]
        })
    }
}
